/**
 * İade Onayları — ürün değişiminde AÇILMIŞ (kayıp) iadeler müdür onayı bekler.
 *
 * Açılmamış iade serbest (stoğa geri, otomatik APPROVED). Açılmış iade = kayıp
 * (WASTED) → PENDING; müdür burada onaylar/reddeder. Onayda bedelden düşülür.
 * İade = suistimal riski → onay yalnız OWNER/MANAGER.
 */
import {
  Badge, Button, Card, Group, Loader, Modal, SegmentedControl,
  Stack, Table, Text, Textarea, Tooltip,
} from "@mantine/core";
import { IconCheck, IconClock, IconX, IconArrowsExchange } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import {
  useLineReturns, useApproveLineReturn, useRejectLineReturn,
} from "../../../shared/api";
import { MoneyDisplay, PageHeader } from "../../../shared/ui";

const STATUS_COLOR: Record<string, string> = {
  PENDING: "orange",
  APPROVED: "green",
  REJECTED: "red",
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: "Onay Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
};
const REASON_LABEL: Record<string, string> = {
  CHANGED_MIND: "Fikir Değiştirdi",
  WRONG_ITEM: "Yanlış Ürün",
  QUALITY: "Kalite / Beğenmedi",
  OTHER: "Diğer",
};

export function LineReturnsPage() {
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const { data, isLoading } = useLineReturns(
    statusFilter === "ALL" ? {} : { status: statusFilter },
  );
  const approve = useApproveLineReturn();
  const reject = useRejectLineReturn();

  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);

  const returns = data?.results || data || [];
  const pendingCount = returns.filter((r: any) => r.status === "PENDING").length;

  const handleApprove = async (ret: any) => {
    try {
      await approve.mutateAsync(ret.id);
      notifications.show({
        title: "İade Onaylandı",
        message: `${ret.product_name} bedelden düşüldü`,
        color: "green",
        icon: <IconCheck />,
      });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await reject.mutateAsync({ id: rejectTarget.id, reason: rejectReason });
      notifications.show({
        title: "Reddedildi",
        message: "İade iptal edildi — bedel/stok değişmedi",
        color: "gray",
      });
      closeReject();
      setRejectTarget(null);
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="🔄 İade Onayları"
        subtitle="Ürün değişiminde AÇILMIŞ (kayıp) iadeler müdür onayı bekler. Açılmamış iadeler serbest (stoğa geri)."
        actions={
          pendingCount > 0 ? (
            <Badge size="lg" color="orange" leftSection={<IconClock size={14} />}>
              {pendingCount} bekleyen
            </Badge>
          ) : null
        }
      />

      <SegmentedControl
        value={statusFilter}
        onChange={setStatusFilter}
        data={[
          { label: "Bekleyen", value: "PENDING" },
          { label: "Onaylanan", value: "APPROVED" },
          { label: "Reddedilen", value: "REJECTED" },
          { label: "Tümü", value: "ALL" },
        ]}
      />

      {isLoading ? <Loader /> : returns.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center">
            {statusFilter === "PENDING" ? "Bekleyen iade onayı yok 🎉" : "Kayıt bulunamadı"}
          </Text>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={780}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>İade Edilen</Table.Th>
                <Table.Th ta="right">Porsiyon</Table.Th>
                <Table.Th>Sebep</Table.Th>
                <Table.Th>Durum (ürün)</Table.Th>
                <Table.Th>Değişen</Table.Th>
                <Table.Th ta="right">Bedel</Table.Th>
                <Table.Th ta="right">Maliyet</Table.Th>
                <Table.Th>Onay</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {returns.map((r: any) => (
                <Table.Tr key={r.id}>
                  <Table.Td fw={500}>
                    {r.product_name}
                    {r.notes ? <Text size="xs" c="dimmed">"{r.notes}"</Text> : null}
                  </Table.Td>
                  <Table.Td ta="right" fw={600}>{r.return_quantity}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="indigo">
                      {REASON_LABEL[r.reason_code] || r.reason_code}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="dot" color={r.opened ? "red" : "green"}>
                      {r.opened ? "Açılmış (kayıp)" : "Açılmamış (geri)"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {r.exchanged_for_name ? (
                      <Tooltip label="Yeni eklenen ürün">
                        <Group gap={4} wrap="nowrap">
                          <IconArrowsExchange size={13} />
                          <Text size="xs">{r.exchanged_for_name}</Text>
                        </Group>
                      </Tooltip>
                    ) : <Text size="xs" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td ta="right">
                    {r.status === "APPROVED"
                      ? <MoneyDisplay minor={r.amount_minor} />
                      : <Text size="xs" c="dimmed">onayda</Text>}
                  </Table.Td>
                  <Table.Td ta="right">
                    {r.status === "APPROVED"
                      ? <MoneyDisplay minor={r.cost_minor} />
                      : "—"}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[r.status]}>
                      {STATUS_LABEL[r.status] || r.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {r.status === "PENDING" ? (
                      <Group gap="xs">
                        <Button
                          size="xs" color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => handleApprove(r)}
                          loading={approve.isPending}
                        >
                          Onayla
                        </Button>
                        <Button
                          size="xs" color="red" variant="light"
                          leftSection={<IconX size={14} />}
                          onClick={() => {
                            setRejectTarget(r);
                            setRejectReason("");
                            openReject();
                          }}
                        >
                          Reddet
                        </Button>
                      </Group>
                    ) : r.status === "REJECTED" && r.rejection_reason ? (
                      <Text size="xs" c="dimmed">"{r.rejection_reason}"</Text>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal opened={rejectOpened} onClose={closeReject} title="İadeyi Reddet" centered>
        <Stack>
          <Text size="sm" c="dimmed">
            {rejectTarget?.product_name} iadesi reddedilecek — bedel/stok
            değişmez. Sebep (opsiyonel):
          </Text>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            placeholder="Örn. ürün açılmamış, stoğa geri alınmalı"
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeReject}>Vazgeç</Button>
            <Button color="red" onClick={handleReject} loading={reject.isPending}>
              Reddet
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
